import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { BarcodeNode, MatcodeNode, SectionNode, defaultExpanded, needsAttention } from "./treeModel";

type FolderNode = BarcodeNode | MatcodeNode | SectionNode;

const childrenOf = (node: FolderNode): FolderNode[] =>
  node.kind === "barcode" ? node.matcodes : node.kind === "matcode" ? node.sections : [];

function walk(tree: BarcodeNode[], visit: (node: FolderNode, ancestors: string[]) => void) {
  const step = (node: FolderNode, ancestors: string[]) => {
    visit(node, ancestors);
    childrenOf(node).forEach((child) => step(child, [...ancestors, node.id]));
  };
  tree.forEach((b) => step(b, []));
}

/**
 * Owns which folders are open.
 *
 * A node keeps whatever the uploader last chose for it. A node seen for the first time
 * takes the attention-first default, and a node that *becomes* a problem later — product
 * validation resolves to "not found" well after the drop — opens itself along with its
 * ancestors, so work never appears behind a closed folder.
 */
export function useTreeExpansion(tree: BarcodeNode[]) {
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const seen = useRef<Map<string, boolean>>(new Map());

  useEffect(() => {
    const defaults = defaultExpanded(tree);
    const opens: string[] = [];
    const next = new Map<string, boolean>();

    walk(tree, (node, ancestors) => {
      const attention = needsAttention(node.stats);
      next.set(node.id, attention);
      const had = seen.current.get(node.id);
      if (had === undefined) {
        if (defaults.has(node.id)) opens.push(node.id);
      } else if (!had && attention) {
        opens.push(node.id, ...ancestors);
      }
    });

    seen.current = next;
    if (opens.length === 0) return;
    setExpanded((prev) => {
      const merged = new Set(prev);
      let changed = false;
      for (const id of opens) if (!merged.has(id)) { merged.add(id); changed = true; }
      return changed ? merged : prev;
    });
  }, [tree]);

  const allIds = useMemo(() => {
    const ids: string[] = [];
    walk(tree, (node) => ids.push(node.id));
    return ids;
  }, [tree]);

  const toggle = useCallback((id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const setOpen = useCallback((id: string, open: boolean) => {
    setExpanded((prev) => {
      if (prev.has(id) === open) return prev;
      const next = new Set(prev);
      if (open) next.add(id);
      else next.delete(id);
      return next;
    });
  }, []);

  const expandAll = useCallback(() => setExpanded(new Set(allIds)), [allIds]);
  const collapseAll = useCallback(() => setExpanded(new Set()), []);

  const allOpen = allIds.length > 0 && allIds.every((id) => expanded.has(id));

  return { expanded, toggle, setOpen, expandAll, collapseAll, allOpen };
}
